import { Connection } from './connection';
import Flatten, { Segment } from '@flatten-js/core';
import { AreaState } from './area-state';

/**
 * Object representing the storage of all the connection present in the scene.
 * It is shared by all the area states and edit state.
 */
export class ConnectionStorage {
  constructor(options) {
    ({ editState: this.editState } = options);
    this.connections = [];
    this.breakerBoards = [];
  }
  /**
   * From the raw JSON representation of two connection items creates the Connection object.
   * Because always two areas has the same Connection object, it is added into the storage only if it is not present yet.
   * @param  {Array.<Object>} rawConnectionItems
   */
  createFromRawDataIfNeeded(rawConnectionItems) {
    if (rawConnectionItems.length !== 2) {
      throw Error('Connection object has not exactly two items');
    }

    const newConnection = new Connection({
      areaA: this.editState.findAreaById(rawConnectionItems[0].areaId),
      areaB: this.editState.findAreaById(rawConnectionItems[1].areaId),
      areaIndexA: rawConnectionItems[0].index,
      areaIndexB: rawConnectionItems[1].index,
    });

    for (const connection of this.connections) {
      if (connection.equals(newConnection)) {
        return;
      }
    }

    this.connections.push(newConnection);
  }

  getConnectedAreas(areaState, borderIndex) {
    const areas = [];

    for (const connection of this.connections) {
      if (connection.hasConnectedAreaOnIndex(areaState, borderIndex)) {
        areas.push(connection.getConnectedArea(areaState));
      }
    }

    return areas;
  }

  getAllConnectedAreas(areaState) {
    const areas = [];

    for (const connection of this.connections) {
      const area = connection.getConnectedArea(areaState);
      if (area) {
        areas.push(area);
      }
    }

    return areas;
  }

  getConnections(areaState) {
    const connections = [];
    for (const connection of this.connections) {
      if (connection.hasConnectedArea(areaState)) {
        connections.push(connection);
      }
    }
    return connections;
  }

  getConnectionsOnIndex(areaState, index) {
    const connections = [];

    for (const connection of this.connections) {
      if (connection.hasConnectedAreaOnIndex(areaState, index)) {
        connections.push(connection);
      }
    }

    return connections;
  }

  createSplitConnections(areaA, areaB, splitSegment) {
    if (!this.hasConnectionBetweenAreas(areaA, areaB)) {
      const connections = this._createSplitConnections(areaA, areaB, splitSegment);
      if (connections && connections.length) {
        this.connections.push(connections[0]);
      }

      return connections[0];
    } else {
      return [];
    }
  }

  removeConnectionsOf(area) {
    const filtered = this.connections.filter(con => {
      return !con.hasConnectedArea(area);
    });
    this.connections = filtered;
  }

  removeConnectionsBetweenAreas(areaA, areaB) {
    const filtered = this.connections.filter(con => {
      return !(con.hasConnectedArea(areaA) && con.hasConnectedArea(areaB));
    });
    this.connections = filtered;
  }

  hasConnectionBetweenAreas(areaA, areaB) {
    return !!this.getConnectionBetweenAreas(areaA, areaB);
  }

  getConnectionBetweenAreas(areaA, areaB) {
    const ids = [areaA.id, areaB.id];

    for (const connection of this.connections) {
      if (ids.includes(connection.getAreas()[0].id) && ids.includes(connection.getAreas()[1].id)) {
        return connection;
      }
    }

    return false;
  }

  getDescriptors(areaState) {
    const connections = this.getConnections(areaState);
    const descriptors = [];
    for (const connection of connections) {
      descriptors.push(connection.toJSON());
    }
    return descriptors;
  }

  /**
   * Takes the two area states and one segment. Creates the appropriate connection for the two areas on the segment.
   * If there is no connection between the areas, return false
   * @param  {AreaState} areaA
   * @param  {AreaState} areaB
   * @param  {Flatten.Segment} splitSegment
   * @returns {Array.<Connection>|false}
   */
  _createSplitConnections(areaA, areaB, splitSegment) {
    const connections = [];
    const borderIndexesOfA = [];
    const borderIndexesOfB = [];

    // get border indexes of areaA sharing the splitSegment
    const closedPointsA = [...areaA.originalPoints, areaA.originalPoints[0]];
    for (let i = 0; i < closedPointsA.length - 1; i++) {
      const segment = new Segment(closedPointsA[i], closedPointsA[i + 1]);
      if (
        (segment.contains(splitSegment.ps) && segment.contains(splitSegment.pe)) ||
        (splitSegment.contains(segment.ps) && splitSegment.contains(segment.pe))
      ) {
        borderIndexesOfA.push(i);
      }
    }

    // get border indexes of areaB sharing the splitSegment
    const closedPointsB = [...areaB.originalPoints, areaB.originalPoints[0]];
    for (let i = 0; i < closedPointsB.length - 1; i++) {
      const segment = new Segment(closedPointsB[i], closedPointsB[i + 1]);
      if (
        (segment.contains(splitSegment.ps) && segment.contains(splitSegment.pe)) ||
        (splitSegment.contains(segment.ps) && splitSegment.contains(segment.pe))
      ) {
        borderIndexesOfB.push(i);
      }
    }

    if (borderIndexesOfA.length === 1 && borderIndexesOfB.length === 1) {
      connections.push(
        new Connection({
          areaIndexA: borderIndexesOfA[0],
          areaIndexB: borderIndexesOfB[0],
          areaA: areaA,
          areaB: areaB,
        })
      );
    } else if (!borderIndexesOfA.length || !borderIndexesOfB) {
      // no connection at all
      return false;
    } else if (borderIndexesOfA.length > 1 || borderIndexesOfB.length > 1) {
      throw Error('Probably there was a Hole splitted, cannot handle so far');
    }
    return connections;
  }
}
